import {useState, useCallback} from 'react';
import API from '@/APIs/API';
import {BankVerificationState, BankVerificationRequest} from '@/APIs/types';

interface BankVerificationResult {
    verificationId: string;
    state: BankVerificationState;
    failureCode?: string;
    failureMessage?: string;
    accountHash?: string;
}

export function useBankVerification() {
    const [verificationResult, setVerificationResult] = useState<BankVerificationResult | null>(null);
    const [overrideConfirmed, setOverrideConfirmed] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);

    const verifyBankAccount = useCallback(
        async (requestData: BankVerificationRequest) => {
            if (!requestData.routingNumber || !requestData.accountNumber) {
                return {success: false, result: null};
            }

            setIsVerifying(true);
            try {
                const result = await API.VerifyBankAccount(requestData);
                if (!result) {
                    return {success: false, result: null};
                }
                setVerificationResult(result);
                // Only check if can proceed for PASS state
                // For PREVENT_NEXT_WITH_OVERRIDE, user needs to check override first
                const canProceed = result.state === BankVerificationState.PASS;
                return {success: canProceed, result};
            } catch (error) {
                // On error, allow proceeding
                return {success: true, result: null};
            } finally {
                setIsVerifying(false);
            }
        },
        [overrideConfirmed],
    );

    const resetVerification = useCallback(() => {
        setVerificationResult(null);
        setOverrideConfirmed(false);
    }, []);

    const canProceed = useCallback(() => {
        if (!verificationResult) return false;
        if (verificationResult.state === BankVerificationState.PASS) return true;
        if (verificationResult.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE) {
            return overrideConfirmed;
        }
        return false;
    }, [verificationResult, overrideConfirmed]);

    return {
        verificationResult,
        overrideConfirmed,
        setOverrideConfirmed,
        isVerifying,
        verifyBankAccount,
        resetVerification,
        canProceed,
    };
}
